import React, { useEffect, useState, useContext } from "react";
import { useFetchApi } from "../../../hooks/useFetchApi";
import { DynamicTabs } from "./tabsSelector";
import { VscListFlat } from "react-icons/vsc";
import Chart from "react-apexcharts";
import {
  convertToInternationalCurrencySystem,
  captialCase,
} from "../../../helper";
import { GRAPH_TYPES } from "../../../store/types";
import Comparison from "../../../components/GraphTypes/Comparison";
import { StateContext } from "../../../store/context";
import {
  mergeAreaChartOptions,
  mergeChartOptions,
} from "../chartTheme";

interface Profile {
  company_name: string;
  profile: string;
  company_logo: string;
  id: string;
  ticker_symbol: string;
}

interface Category {
  name: string;
  id: number;
  order_no: number;
  snapshots: Array<{
    id: number;
    graph_name: string;
    graph_data: string;
    graph_category_id: number;
    order_no: number;
  }>;
}

interface DynamicTabProps {
  name?: string;
  snapshots: any[];
  primaryStock: string[];
  selectedStocks: any[];
  staticGraphs?: any;
  compareResults: any;
  compareOtherStockResults: any;
}

const DynamicTab: React.FC<DynamicTabProps> = ({
  name,
  snapshots,
  primaryStock,
  selectedStocks,
  staticGraphs,
  compareResults,
  compareOtherStockResults,
}) => {
  const {
    execute: generateGraphs,
    loading: graphLoading,
    response: graphData,
  } = useFetchApi("/api/dynamic-graph");
  const [chartData, setChartData] = useState<any>([]);
  const { baseCurrency } = useContext(StateContext);

  useEffect(() => {
    setChartData([]);
    if (
      snapshots.length > 0 &&
      (selectedStocks.length > 0 || primaryStock.length > 0)
    ) {
      snapshots.forEach(async (snapshot: any) => {
        if (snapshot.graph_data) {
          const parsedSnapshot = JSON.parse(snapshot.graph_data);
          const { selectedMetrics, graphType, period } = parsedSnapshot;

          generateGraphs({
            selectedMetrics,
            selectedStocks:
              name === "Comparable Stocks"
                ? [...primaryStock, ...selectedStocks]
                : primaryStock,
            filter: graphType,
            period,
            order_no: snapshot.order_no,
            categoryName: name,
            baseCurrency,
            graphName: snapshot.graph_name,
          });
        } else {
          setChartData((prev: any) =>
            [
              ...prev,
              ...(staticGraphs && staticGraphs[snapshot.graph_name]
                ? [
                    {
                      Component: staticGraphs[snapshot.graph_name],
                      order_no: snapshot.order_no,
                    },
                  ]
                : []),
            ].sort((a, b) => a.order_no - b.order_no)
          );
        }
      });
    }
  }, [selectedStocks]);

  useEffect(() => {
    if (!graphLoading && graphData) {
      if (
        !chartData.some((chart: any) => chart.order_no === graphData.order_no)
      ) {
        setChartData((prev: any) =>
          [...prev, graphData].sort((a, b) => a.order_no - b.order_no)
        );
      }
    }
  }, [graphData, graphLoading]);

  if (chartData.length === 0) {
    return <NoDataDetails />;
  }

  return (
    <div className="flex flex-col w-full h-full">
      {chartData.map((chart: any) => {
        if (!chart.series) {
          const { Component } = chart;
          return (
            <Component
              compareResults={compareResults}
              compareOtherStockResults={compareOtherStockResults}
            />
          );
        }

        if (name === "Comparable Stocks" && chart.categories?.length > 0) {
          return (
            <Comparison
              data={{
                series: chart.series,
                categories: chart.categories,
                name: chart.name,
                baseCurrency: baseCurrency,
              }}
            />
          );
        }

        const isAreaMetric =
          name === "Profitability" &&
          chart.series[0].metricGraphType === GRAPH_TYPES.PERCENTAGES;
        const mergeOptions = isAreaMetric
          ? mergeAreaChartOptions
          : mergeChartOptions;

        return (
          <div
            key={chart.order_no}
            className="p-4 w-full vi-glass-card"
          >
            <h5 className="text-lg font-medium mb-4 text-[color:var(--vi-text)]">
              {captialCase(chart.name)}
            </h5>
            <Chart
              options={mergeOptions({
                chart: {
                  type: "line",
                  // one options object gets built per chart in this loop —
                  // give each its own id or ApexCharts treats them as the
                  // same instance
                  id: `dynamic-graph-${chart.order_no ?? chart.name}`,
                },
                tooltip: {
                  shared: true,
                  hideEmptySeries: false,
                  y: {
                    formatter: (value: any) => {
                      if (value === null || value === undefined || isNaN(Number(value))) {
                        return "N/A";
                      }
                      const metricGraphType = chart.series[0].metricGraphType;
                      switch (metricGraphType) {
                        case GRAPH_TYPES.PERCENTAGES:
                          return `${value}%`;
                        case GRAPH_TYPES.ABSOLUTE_VALUES:
                          return convertToInternationalCurrencySystem(value);
                        case GRAPH_TYPES.DAYS:
                          return `${Number(value).toFixed(0)} days`;
                        case GRAPH_TYPES.MULTIPLES:
                          return `${value}x`;
                        default:
                          return value;
                      }
                    },
                  },
                },
                dataLabels: {
                  enabled: true,
                  formatter: (value: any) => {
                    // Hide label for null/undefined/NaN values
                    if (value === null || value === undefined || isNaN(Number(value))) {
                      return "";
                    }
                    const metricGraphType = chart.series[0].metricGraphType;
                    switch (metricGraphType) {
                      case GRAPH_TYPES.PERCENTAGES:
                        return `${Number(value).toFixed(0)}%`;
                      case GRAPH_TYPES.ABSOLUTE_VALUES:
                        return convertToInternationalCurrencySystem(value);
                      case GRAPH_TYPES.DAYS:
                        return `${value} days`;
                      case GRAPH_TYPES.MULTIPLES:
                        return `${value}x`;
                      default:
                        return value;
                    }
                  },
                  offsetY: -10,
                },
                yaxis: {
                  labels: {
                    formatter: (value: any) => {
                      if (value === null || value === undefined || isNaN(Number(value))) {
                        return "";
                      }
                      const metricGraphType = chart.series[0].metricGraphType;
                      switch (metricGraphType) {
                        case GRAPH_TYPES.PERCENTAGES:
                          return `${Number(value).toFixed(0)}%`;
                        case GRAPH_TYPES.ABSOLUTE_VALUES:
                          return `${baseCurrency} ${convertToInternationalCurrencySystem(
                            value
                          )}`;
                        case GRAPH_TYPES.DAYS:
                          return `${Number(value).toFixed(0)} days`;
                        case GRAPH_TYPES.MULTIPLES:
                          return `${Number(value).toFixed(2)}x`;
                        default:
                          return value;
                      }
                    },
                  },
                },
                xaxis: {
                  categories: chart.categories,
                },
              })}
              series={
                isAreaMetric
                  ? chart.series.map((s: any) => ({ ...s, type: "area" }))
                  : chart.series
              }
              height={550}
            />
          </div>
        );
      })}
    </div>
  );
};

interface ComparativeInsightsProps {
  selectedStocks: string[];
  categories: any;
  stocksToCompare?: string[];
  allowed?: string[];
  KEYS: {
    DYNAMIC_GRAPHS: string;
  };
  isAllowed: (keys: string[], allowed: string[]) => boolean;
  primaryStock: string[];
  staticGraphs?: any;
  compareResults: any;
  compareOtherStockResults: any;
  compareLoading: boolean;
}

export const ComparativeInsights = (props: ComparativeInsightsProps) => {
  const {
    selectedStocks,
    categories,
    stocksToCompare = [],
    allowed = [],
    KEYS,
    isAllowed,
    primaryStock,
    staticGraphs,
    compareResults,
    compareOtherStockResults,
    compareLoading,
  } = props;

  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const { execute, response, loading } = useFetchApi("/api/company/profiles", {
    stock_ids: selectedStocks?.map((stock: any) => stock?.id),
  });
  const [mainTabs, setMainTabs] = useState<any>([]);

  useEffect(() => {
    if (selectedStocks?.length > 0) {
      execute();
    }
  }, [selectedStocks]);

  useEffect(() => {
    if (!loading && response) {
      setProfiles(response);
    }
  }, [response]);

  useEffect(() => {
    if (profiles && categories) {
      // newly added quick fix
      const filteredAndSortedCategories = [...categories]
        .sort((a, b) => a.order_no - b.order_no)
        .filter((category) =>
          ["Comparable Stocks", "Company Profile"]?.includes(category.name)
        );

      filteredAndSortedCategories?.sort((a, b) => {
        if (a.name === "Company Profile") return -1;
        if (b.name === "Company Profile") return 1;
        return 0;
      });

      const tabs = filteredAndSortedCategories?.map((category) => ({
        key: category.id.toString(),
        label: category.name,
        subTabs:
          category.name === "Company Profile"
            ? profiles?.map((company, index) => ({
                key: `${category.id}-${index + 1}`,
                label: company.ticker_symbol,
                content:
                  loading || compareLoading
                    ? renderSkeleton()
                    : renderProfile(company),
              }))
            : category.snapshots.map((snapshot: any, index: any) => ({
                key: `${category.id}-${index + 1}`,
                label: snapshot.graph_name,
                content: (
                  <DynamicTab
                    name={category.name}
                    snapshots={[snapshot]}
                    primaryStock={primaryStock}
                    selectedStocks={selectedStocks}
                    staticGraphs={staticGraphs}
                    compareResults={compareResults}
                    compareOtherStockResults={compareOtherStockResults}
                  />
                ),
              })),
      }));

      setMainTabs(tabs);
    }
  }, [profiles, categories, stocksToCompare, allowed]);

  return (
    <div className="w-full !h-full min-h-[400px]">
      {loading || compareLoading ? (
        renderSkeleton()
      ) : selectedStocks?.length === 0 ? (
        <NoDataDetails />
      ) : (
        <DynamicTabs
          defaultActiveTab="23"
          defaultSubTab="23-1"
          mainTabs={mainTabs}
        />
      )}
    </div>
  );
};
const renderProfile = (profile: Profile) => (
  <div className="vi-glass-card flex flex-col lg:flex-row items-start justify-between gap-x-6 w-full h-full p-4">
    <div className="min-w-[96px] h-[96px]">
      {profile.company_logo ? (
        <img
          src={`/${profile.company_logo}`}
          alt={profile.company_name}
          className="object-contain w-full h-full rounded-md"
        />
      ) : (
        <div className="w-full h-full bg-gray-200 rounded-md flex items-center justify-center text-gray-500">
          No Image
        </div>
      )}
    </div>

    <div className="flex flex-col items-start gap-y-2">
      <h1 className="text-[16px] font-medium text-[#101828]">
        {profile.company_name}
      </h1>
      <p className="font-normal text-sm text-[#667085]">{profile.profile}</p>
    </div>
  </div>
);

const NoDataDetails = () => (
  <div className="vi-glass-card w-full flex flex-col items-center justify-center h-[400px]">
    <div className="h-12 w-12 bg-[#F0F6FF] rounded-full flex items-center justify-center mb-4">
      <VscListFlat className="text-[#0075E3]" />
    </div>
    <h1 className="text-[16px] font-[500] pb-1">
      {" "}
      Select a stock to uncover its financial story
    </h1>
    {/* <p className="font-normal text-sm text-center">
      Select one or more stocks to display the resulting information here.
    </p> */}
  </div>
);

const renderSkeleton = () => (
  <div className="flex items-start justify-between gap-x-6 w-full h-full animate-pulse">
    <div className="min-w-[96px] h-[96px] bg-gray-200 rounded-md"></div>
    <div className="flex flex-col items-start gap-y-2 w-full">
      <div className="w-3/4 h-4 bg-gray-200 rounded"></div>
      <div className="w-full h-3 bg-gray-200 rounded"></div>
      <div className="w-5/6 h-3 bg-gray-200 rounded"></div>
    </div>
  </div>
);
